import { inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import type { IAuthProvider } from "ui-common/services/auth/types";
import type { ISseService } from "ui-common/services/sse/types";
import type { WorldMutatedEvent, PostMutatedEvent } from "ui-common/services/sse/sse-events";
import type { WorldsQueryResult } from "api-contracts/rpc/worlds-query";
import type { CreateWorldParams } from "api-contracts/rpc/create-world";
import type { UpdateWorldParams } from "api-contracts/rpc/update-world";
import type { IWritableBackofficeWorldsState, IBackofficeWorldsService } from "./types";

export class BackofficeWorldsService implements IBackofficeWorldsService {
  @inject<IWritableBackofficeWorldsState>("writableBackofficeWorldsState")
  private state!: IWritableBackofficeWorldsState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<IAuthProvider>("authProvider")
  private authProvider!: IAuthProvider;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  private pendingLoad: Promise<void> | undefined;
  private unsubscribeSse: (() => void) | null = null;

  loadWorlds(): void {
    if (!this.authProvider.isAuthenticated()) return;
    if (this.pendingLoad) return;

    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  reloadWorlds(): void {
    this.pendingLoad = undefined;
    this.state.loading.set(true);
    this.pendingLoad = this.doLoad();
  }

  private async doLoad(): Promise<void> {
    try {
      const result = await this.rpcService.call<WorldsQueryResult>("worldsQuery", undefined);
      this.state.worlds.set(result);
      this.subscribe();
    } catch (error) {
      console.error("[BackofficeWorldsService] Error loading worlds:", error);
    } finally {
      this.state.loading.set(false);
      this.pendingLoad = undefined;
    }
  }

  private subscribe(): void {
    if (this.unsubscribeSse) return;

    const unsubWorld = this.sseService.on<WorldMutatedEvent>("world-mutated", () => {
      this.reloadWorlds();
    });

    const unsubPost = this.sseService.on<PostMutatedEvent>("post-mutated", () => {
      this.reloadWorlds();
    });

    this.unsubscribeSse = () => {
      unsubWorld();
      unsubPost();
    };
  }

  async createWorld(params: CreateWorldParams): Promise<void> {
    await this.rpcService.call("createWorld", params);
    await this.doLoad();
  }

  async updateWorld(params: UpdateWorldParams): Promise<void> {
    await this.rpcService.call("updateWorld", params);
    await this.doLoad();
  }

  async deleteWorld(worldId: string): Promise<void> {
    await this.rpcService.call("deleteWorld", { worldId });
    await this.doLoad();
  }
}
